import { createUseStyles } from 'react-jss';
import { Drawer } from '../components/drawer/Drawer.tsx';
import { DrawerState } from '../components/drawer/DrawerState.ts';
import { ILocation } from '../support/model/ILocation.ts';
import { useSubscription } from '@autopylot-internal/state';
import { useInstance } from '../support/ioc/useInstance';
import { MissionsState } from '../missions/model/MissionsState.ts';
import { LocationSearch } from '../components/search/LocationSearch.tsx';
import { LocationSearchService } from './location/LocationSearchService.ts';
import { ISuggestion } from '../support/model/ISuggestion.ts';
import { useEffect, useRef, useState } from 'react';
import { usePageView } from '../support/tracking/usePageView.tsx';
import { IMissionCreationSource, NewMissionState } from '../missions/model/NewMissionState.ts';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { ICoordinate } from '../support/model/ICoordinate.ts';
import { FeedbackAlert } from '../app/FeedbackAlert.tsx';
import { MissionPinAndRadiusOnMapViewModel } from './mission-pin-and-radius/MissionPinAndRadiusOnMapViewModel.ts';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';
import { ClearPreMissionWeatherState } from '../weather/components/ClearPreMissionWeatherState.tsx';
import { NewMissionTrackingHelper } from '../mission/NewMissionTrackingHelper.ts';
import { BufferedEventBus } from '../events/BufferedEventBus.ts';
import { AutoPylotPrincipal } from '../auth/AutoPylotPrincipal.ts';
import { RecentMissions } from './recents/RecentMissions.tsx';
import { EditableMissionState } from '../mission/model/EditableMissionState.ts';

const DRAWER_MIN_HEIGHT = 90; // minimized drawer height on mobile
const DRAWER_DEFAULT_HEIGHT = 360; // initial drawer height on mobile
const DRAWER_EXPANDED_HEIGHT = 500; // expanded drawer height on desktop

const FEET_PER_METER = 3.28084;

// persists across mount/unmount cycles so we can restore the user's drawer height
let savedNoMissionDrawerHeight: number | undefined;

const useStyles = createUseStyles({
    floatingPanel: {
        marginTop: 20,
        backgroundColor: 'white',
        borderRadius: 12,
        boxShadow: '0px 0px 4px 1px rgb(0 0 0 / 15%)',
        overflow: 'hidden',
        paddingBottom: 12,
        '& $searchSection': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }
    },
    searchSection: {}
});

export const NoMissionMapDrawer: React.FC<{setNewMission: (mission: NewMissionState) => void, beginCreatingMission: (params: { trackingFrom: IMissionCreationSource }) => Promise<NewMissionState>}> = ({ setNewMission, beginCreatingMission }) => {
    usePageView('Map');

    const classes = useStyles();
    const tracker = useInstance(TrackingService);

    const { isLargerScreen } = useBreakpoints();

    const drawerState = useInstance(DrawerState);
    useSubscription(() => drawerState);
    const locationSearchService = useInstance(LocationSearchService);

    const principal = useInstance(AutoPylotPrincipal);

    const vm = useInstance(MissionsState);
    useSubscription(() => vm);

    const pinAndRadiusViewModel = useInstance(MissionPinAndRadiusOnMapViewModel);
    const trackingHelper = useInstance(NewMissionTrackingHelper);

    const events = useInstance(BufferedEventBus);

    useEffect(() => {
        // on mount we want a blank map, until an address is selected
        pinAndRadiusViewModel.remove();
    }, [pinAndRadiusViewModel]);

    const [hasResults, setHasResults] = useState(false);

    const onAddressSelected = async (location?: ILocation) => {
        if (location) {
            const mission = await beginCreatingMission({ trackingFrom: 'search' });
            mission.setAddress(location.address);

            mission.setCenter(location.coordinates).then(() => {
                // this has to happen after awaiting setCenter(), otherwise radius won't have been created, and canFly etc will be undefined
                trackingHelper.addressSelected({
                    address: location.address,
                    coordinates: location.coordinates,
                    mission: mission
                });

                events.emit('MissionCreationStarted');
            });

            setNewMission(mission);
        } else {
            // user clicked the "X" to clear the search
            collapseDrawer();
        }
    };

    const onRecentMissionSelected = async (sourceMission: EditableMissionState) => {
        const mission = await beginCreatingMission({ trackingFrom: 'recents' });

        // Pre-fill address from the source mission
        mission.setAddress(sourceMission.state.address);

        // Pre-fill center and radius from the source mission
        const center = sourceMission.getLocation();
        const radiusMeters = sourceMission.getMissionRadius().getMissionRadius().properties.radiusMeters;
        const radiusFeet = Math.round(Math.round(radiusMeters * FEET_PER_METER) / 10) * 10;

        mission.setCenterAndRadius({ center, radiusFeet }).then(() => {
            events.emit('MissionCreationStarted');
        });

        setNewMission(mission);
    };

    const onClick = () => {
        // on phone, expand/collapse based on clicking
        if (!isLargerScreen) {
            expandDrawer();
        }
    };

    const onChange = (text: any) => {
        // keep track of when we have results, so we can show/hide recents
        setHasResults(Boolean(text));

        // on desktop, expand/collapse based on typing
        if (isLargerScreen) {
            if (text) {
                expandDrawer();
            } else {
                collapseDrawer();
            }
        }
    };

    const matchesToReturn = !isLargerScreen ? 5 : 10;

    const handleSearch = async (searchText: string): Promise<ISuggestion[]> => {
        onChange(searchText);
        return await locationSearchService.suggest({ searchText, matchesToReturn, sessionToken: principal.userId });
    };

    const handleSelect = async (suggestion?: ISuggestion) => {
        if (suggestion) {
            const coordinates = await locationSearchService.retrieve({ id: suggestion.id, sessionToken: principal.userId });
            onAddressSelected({ address: suggestion.address, coordinates });
        } else {
            onAddressSelected(undefined);
        }
    };
    const expandDrawer = () => {
        if (isLargerScreen) {
            drawerState.setHeight(DRAWER_EXPANDED_HEIGHT);
        } else {
            // when tapped on phone, expand drawer enough to show all results,
            // but no more than that, to ensure cursor is not pushed off the top
            // when keyboard appears (this problem observed on iphone 11 pro)
            drawerState.setHeight(DRAWER_DEFAULT_HEIGHT);
        }
    };

    const collapseDrawer = () => {
        if (isLargerScreen) {
            drawerState.setHeight(DRAWER_DEFAULT_HEIGHT);
        } else {
            drawerState.setHeight(0);
        }
    };

    const onScroll = () => {
        tracker.track('Flight Checklist Scrolled');
    };

    // intended for use by devs, coordinates must be entered as json {"lat":45,"lng":-73}
    // use coordinates entered to drop a pin at that location
    const onCoordinatesEntered = async (coordinates: ICoordinate) => {
        const mission = await beginCreatingMission({ trackingFrom: 'search' });
        mission.setCenter(coordinates);
        const location = await locationSearchService.reverseLookup(coordinates);
        if (location) {
            mission.setAddress(location.address);
        }
        setNewMission(mission);
    };

    // different margin for side drawer
    const marginTop = (isLargerScreen ? 0 : 5);

    const isFloating = isLargerScreen && drawerState.state.height <= DRAWER_DEFAULT_HEIGHT;

    // If the user had minimized the drawer before navigating away (e.g. to drop a pin),
    // preserve their minimized height so they don't have to re-minimize on every return.
    // We save height on unmount (before another drawer overwrites it) and restore on next mount.
    const savedHeightRef = useRef<number | undefined>(savedNoMissionDrawerHeight);
    const initialHeight = (savedHeightRef.current !== undefined && savedHeightRef.current === DRAWER_MIN_HEIGHT)
        ? savedHeightRef.current
        : DRAWER_DEFAULT_HEIGHT;

    useEffect(() => {
        return () => { savedNoMissionDrawerHeight = drawerState.state.height; };
    }, [drawerState]);

    // Fade out recents as drawer approaches minimum height on mobile.
    // Use height + delta so the fade tracks the drag in real time (delta is non-zero during dragging).
    const FADE_START = 180;
    const currentHeight = drawerState.state.height + drawerState.state.delta;
    const actualMinHeight = drawerState.state.minHeight;
    const recentsOpacity = isLargerScreen ? 1 : Math.min(1, Math.max(0, (currentHeight - actualMinHeight) / (FADE_START - actualMinHeight)));

    return (
        <>
            <FeedbackAlert />
            <ClearPreMissionWeatherState />

            <Drawer minHeight={DRAWER_MIN_HEIGHT} initialHeight={initialHeight} onScroll={onScroll} minimizeFloating>
                <div className={isFloating ? classes.floatingPanel : undefined}>
                    <div className={classes.searchSection} data-id="location-search-drawer" onClick={onClick} style={{marginTop}}>
                        <LocationSearch onSearch={handleSearch} onSelect={handleSelect} onCoordinates={onCoordinatesEntered} embedded={isFloating}/>
                    </div>
                    {!hasResults &&
                        <div style={{opacity: recentsOpacity}}>
                            <RecentMissions onSelectMission={onRecentMissionSelected} />
                        </div>
                    }
                </div>
            </Drawer>
        </>
    );
};
